import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Film, ArrowLeft, Mail, Send, CheckCircle2, Shield, Trash2, RefreshCw, ExternalLink, Sparkles, Loader2, AlertCircle, Info } from "lucide-react";
import { api } from "@/lib/api";
import { sendContact, contactErr } from "@/lib/contact";

// Fliboe Showo Make — Contact / About / Admin pages.
// Kept in one file so the Showo Make sub-app stays a tidy 3-file surface:
// ShowoMake.jsx (generator), Showo.jsx (login/landing), ShowoMakeExtras.jsx (this).

const SHOWO_TOKEN_KEY = "fliboe_showo_token";
function bootstrap() {
  try {
    if (typeof localStorage === "undefined") return;
    if (!localStorage.getItem(SHOWO_TOKEN_KEY) && localStorage.getItem("fliboe_token")) {
      localStorage.setItem(SHOWO_TOKEN_KEY, localStorage.getItem("fliboe_token"));
    }
  } catch (_) {}
}
if (typeof window !== "undefined") bootstrap();
const auth = () => { bootstrap(); const t = localStorage.getItem(SHOWO_TOKEN_KEY); return t ? { Authorization: `Bearer ${t}` } : {}; };

// Shared shell — matches ShowoMake.jsx so all four pages feel like one product.
function Shell({ title, children, testid }) {
  const isSignedIn = typeof window !== "undefined" && !!localStorage.getItem(SHOWO_TOKEN_KEY);
  return (
    <main className="min-h-screen bg-[#08081a] text-white relative pb-24" data-testid={testid}>
      <div className="absolute inset-0 pointer-events-none -z-0" aria-hidden>
        <img src="https://images.unsplash.com/photo-1478720568477-152d9b164e26?w=1600&q=80" alt="" className="w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-950/70 via-[#08081a]/90 to-[#08081a]" />
      </div>
      <header className="relative z-10 border-b border-white/10">
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/showo/make" className="flex items-center gap-2" data-testid="showo-make-extras-back">
            <ArrowLeft className="w-4 h-4 text-white/60" />
            <Film className="w-5 h-5 text-emerald-400" />
            <span className="fl-display text-lg font-black tracking-tight">Showo <span className="text-emerald-400 text-xs uppercase tracking-widest ml-1">Make</span></span>
          </Link>
          <div className="flex items-center gap-2 text-xs">
            <Link to="/showo/make/about" className="text-white/60 hover:text-emerald-400" data-testid="nav-about">About</Link>
            <span className="text-white/20">·</span>
            <Link to="/showo/make/contact" className="text-white/60 hover:text-emerald-400" data-testid="nav-contact">Contact</Link>
            {!isSignedIn && (<>
              <span className="text-white/20">·</span>
              <Link to="/showo/login?next=/showo/make" className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="nav-signin">Sign in</Link>
            </>)}
          </div>
        </div>
      </header>
      <section className="relative z-10 max-w-3xl mx-auto px-5 pt-10">
        <h1 className="fl-display text-4xl sm:text-5xl font-black tracking-tight leading-[1.02]">{title}</h1>
      </section>
      <section className="relative z-10 max-w-3xl mx-auto px-5 mt-6">{children}</section>
    </main>
  );
}

// ─────────────────────────────── About ───────────────────────────────
export function ShowoMakeAbout() {
  return (
    <Shell title={<>Turn any idea into a<br /><span className="text-emerald-400">short film.</span></>} testid="showo-make-about">
      <div className="fl-card p-6 bg-white/[0.03] border-white/10 space-y-4 text-slate-200">
        <p><strong className="text-emerald-300">Showo Make</strong> is a one-sentence-to-movie generator inside Fliboe Showo. Type a single idea, pick a genre and a language, and we render a vertical 9:16 short film you can share to Showo, TikTok, or Reels.</p>

        <div className="fl-mono text-[10px] uppercase tracking-widest text-emerald-400 pt-2">// How it works</div>
        <ul className="list-disc pl-5 space-y-1.5 text-sm">
          <li><strong>Script</strong> — Claude writes a title, character, and 3-10 scene beats with narration in your chosen language.</li>
          <li><strong>Scenes</strong> — Gemini Nano Banana paints every scene as a cinematic vertical still, threaded with the same character description so the lead stays consistent.</li>
          <li><strong>Narration</strong> — OpenAI TTS-HD voices every scene in 11 languages (English, Hindi, Spanish, Japanese, Arabic, Mandarin, and more).</li>
          <li><strong>Movie</strong> — FFmpeg applies Ken-Burns motion to each scene, syncs the narration, and stitches the final MP4.</li>
          <li><strong>Publish</strong> — Every rendered movie can auto-publish to your Showo feed AND auto-spawn a linked Pulse SEO article so Google finds it.</li>
        </ul>

        <div className="fl-mono text-[10px] uppercase tracking-widest text-emerald-400 pt-2">// Star in your own movie</div>
        <p className="text-sm">Upload one clear photo of your face (with T&amp;C consent) and Showo Make casts you as the lead across every scene. Your photo is scanned by an automated moderator that rejects photos of minors, recognizable public figures, and NSFW content. It is auto-deleted from our servers as soon as the movie renders — only a short text description survives so we can keep the same face across every scene.</p>

        <div className="fl-mono text-[10px] uppercase tracking-widest text-emerald-400 pt-2">// Guardrails</div>
        <ul className="list-disc pl-5 space-y-1.5 text-sm">
          <li>PG-13 only. No gore, no sexual content, no violence against real people.</li>
          <li>No deepfakes of anyone without their consent.</li>
          <li>Admin-only during MVP — paid public tier opening soon.</li>
        </ul>

        <div className="pt-3 flex flex-wrap gap-2">
          <Link to="/showo/make" className="fl-btn !bg-emerald-500 !text-black" data-testid="about-make"><Sparkles className="w-4 h-4" /> Make a movie</Link>
          <Link to="/showo/make/contact" className="fl-btn ghost" data-testid="about-contact"><Mail className="w-4 h-4" /> Contact us</Link>
        </div>
      </div>
    </Shell>
  );
}

// ─────────────────────────────── Contact ─────────────────────────────
export function ShowoMakeContact() {
  const [form, setForm] = useState({ name: "", email: "", topic: "General", message: "" });
  const [sent, setSent] = useState(false); const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try { await sendContact(form, "showo"); setSent(true); } catch (e2) { setErr(contactErr(e2)); }
  };

  return (
    <Shell title={<>Talk to<br /><span className="text-emerald-400">the studio.</span></>} testid="showo-make-contact">
      <div className="grid md:grid-cols-2 gap-4">
        <div className="fl-card p-5 bg-white/[0.03] border-white/10 space-y-2">
          <Mail className="w-5 h-5 text-emerald-400" />
          <div className="text-white font-bold">Send us a message</div>
          <p className="text-sm text-slate-400">Feature requests, press, partnerships, or a bug to report. Use the form below and we'll reply by email.</p>
        </div>
        <div className="fl-card p-5 bg-white/[0.03] border-white/10 space-y-2">
          <Shield className="w-5 h-5 text-emerald-400" />
          <div className="text-white font-bold">Content report</div>
          <p className="text-sm text-slate-400">See a movie that violates our terms — deepfake, minor, harassment? Include the movie link and we'll act within 24h.</p>
        </div>
      </div>

      <form onSubmit={submit} className="fl-card p-6 bg-white/[0.03] border-white/10 space-y-3 mt-4" data-testid="contact-form">
        <div className="grid sm:grid-cols-2 gap-3">
          <input required className="fl-input" placeholder="Your name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="contact-name" />
          <input required type="email" className="fl-input" placeholder="Your email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="contact-email-input" />
        </div>
        <select className="fl-input" value={form.topic} onChange={e => setForm({ ...form, topic: e.target.value })} data-testid="contact-topic">
          <option>General</option>
          <option>Bug report</option>
          <option>Feature request</option>
          <option>Content report</option>
          <option>Press / partnerships</option>
          <option>Access request (public beta)</option>
        </select>
        <textarea required className="fl-input h-32" placeholder="Message" value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} data-testid="contact-message" />
        <button type="submit" className="fl-btn !bg-emerald-500 !text-black w-full justify-center" data-testid="contact-submit">
          <Send className="w-4 h-4" /> Send message
        </button>
        {err && <div className="text-red-400 text-sm" data-testid="contact-error">{err}</div>}
        {sent && (
          <div className="text-emerald-300 text-sm flex items-center gap-1.5" data-testid="contact-sent">
            <CheckCircle2 className="w-4 h-4" /> Message sent. We'll reply to your email soon.
          </div>
        )}
      </form>
    </Shell>
  );
}

// ─────────────────────────────── Admin ───────────────────────────────
const STATUS_COLORS = {
  ready: "text-emerald-300", failed: "text-red-400",
  queued: "text-white/60", scripting: "text-yellow-300",
  imaging: "text-yellow-300", narrating: "text-yellow-300",
  rendering: "text-yellow-300", publishing: "text-yellow-300",
};

export function ShowoMakeAdmin() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const r = await api.get("/showo/make/admin/jobs", { params, headers: auth() });
      setJobs(r.data?.jobs || []);
      setStats(r.data?.stats || null);
    } catch (ex) {
      if (ex?.response?.status === 403) {
        setErr("Admin access required."); navigate("/showo/login?next=/showo/make/admin");
      } else if (ex?.response?.status === 401) {
        navigate("/showo/login?next=/showo/make/admin");
      } else {
        setErr(ex?.response?.data?.detail || "Failed to load admin feed.");
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const removeJob = async (jobId) => {
    if (!window.confirm("Delete this movie, its Showo film, and its GridFS artifacts? This is not reversible.")) return;
    try {
      await api.delete(`/showo/make/admin/jobs/${jobId}`, { headers: auth() });
      setJobs(js => js.filter(j => j.job_id !== jobId));
    } catch (ex) {
      alert(ex?.response?.data?.detail || "Delete failed.");
    }
  };

  const statCards = useMemo(() => stats ? [
    { label: "Total renders", value: stats.total },
    { label: "Successful", value: stats.ready },
    { label: "Failed", value: stats.failed },
    { label: "Face uploads · 24h", value: stats.face_uploads_24h },
  ] : [], [stats]);

  return (
    <Shell title={<>Showo Make<br /><span className="text-emerald-400">admin.</span></>} testid="showo-make-admin">
      <div className="fl-card p-4 bg-white/[0.03] border-white/10 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 text-xs">
          <label className="fl-mono uppercase tracking-widest text-white/60">Filter:</label>
          <select className="fl-input !py-1.5 !px-2 text-xs" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-testid="admin-status-filter">
            <option value="">All</option>
            <option value="ready">Ready</option>
            <option value="failed">Failed</option>
            <option value="rendering">Rendering</option>
            <option value="queued">Queued</option>
          </select>
        </div>
        <button onClick={load} className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="admin-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {statCards.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3" data-testid="admin-stats">
          {statCards.map(s => (
            <div key={s.label} className="fl-card p-3 bg-white/[0.03] border-white/10">
              <div className="fl-mono text-[10px] uppercase tracking-widest text-white/50">{s.label}</div>
              <div className="fl-display text-2xl font-black mt-1">{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {err && <div className="mt-3 text-red-400 text-sm flex items-center gap-1.5" data-testid="admin-err"><AlertCircle className="w-4 h-4" />{err}</div>}

      <div className="mt-4 space-y-2" data-testid="admin-list">
        {loading && jobs.length === 0 ? (
          <div className="text-white/60 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : jobs.length === 0 ? (
          <div className="fl-card p-6 bg-white/[0.03] border-white/10 text-center text-white/60 text-sm flex items-center justify-center gap-2">
            <Info className="w-4 h-4" /> No jobs match this filter yet.
          </div>
        ) : jobs.map(j => (
          <div key={j.job_id} className="fl-card p-3 bg-white/[0.03] border-white/10 flex items-center gap-3" data-testid={`admin-job-${j.job_id}`}>
            <div className="w-14 h-24 rounded-md overflow-hidden bg-black/50 flex-shrink-0">
              {j.cover_url ? (
                <img src={j.cover_url} alt="" className="w-full h-full object-cover" loading="lazy" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/30"><Film className="w-6 h-6" /></div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-white font-semibold truncate text-sm">{j.title || j.prompt}</div>
              <div className="text-[10px] fl-mono uppercase tracking-widest text-white/50 truncate">
                <span className={STATUS_COLORS[j.status] || "text-white/60"}>{j.status}</span>
                {" · "}{j.owner_email || j.owner_id}
                {j.face_id ? " · with face" : ""}
                {j.language && j.language !== "en" ? ` · ${j.language}` : ""}
                {j.film_id ? " · published" : ""}
              </div>
              {j.error && <div className="text-red-400 text-[11px] mt-0.5 truncate">{j.error}</div>}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {j.video_url && (
                <a href={j.video_url} target="_blank" rel="noopener noreferrer" className="fl-btn ghost !py-1 !px-2 text-[10px]" data-testid={`admin-open-${j.job_id}`}>
                  <ExternalLink className="w-3 h-3" /> Watch
                </a>
              )}
              <button onClick={() => removeJob(j.job_id)} className="fl-btn ghost !py-1 !px-2 text-[10px] !text-red-300 hover:!bg-red-500/10" data-testid={`admin-del-${j.job_id}`}>
                <Trash2 className="w-3 h-3" /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </Shell>
  );
}
